import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatINR, formatKM, WHATSAPP_LINK } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { ArrowLeft, MessageCircle, MapPin, Calendar, Gauge, Fuel, Settings2, Palette, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function CarDetail() {
  const { id } = useParams();
  const [car, setCar] = useState(null);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });

  useEffect(() => {
    setLoading(true);
    api.get(`/cars/${id}`).then((r) => setCar(r.data)).finally(() => setLoading(false));
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/inquiries", {
        ...form,
        message: form.message || `Interested in ${car.brand} ${car.model}`,
        car_id: car.id,
        car_title: `${car.year} ${car.brand} ${car.model}`,
      });
      toast.success("Inquiry sent. Our concierge will reach out within 24 hours.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Could not send inquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-gray-500">Loading…</div>;
  if (!car) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <p className="font-display text-3xl">Car not found</p>
        <Link to="/inventory" className="text-sm underline">Back to inventory</Link>
      </div>
    );
  }

  const images = car.images && car.images.length ? car.images : ["https://images.unsplash.com/photo-1503376780353-7e6692767b70?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBjYXIlMjBzbGVlayUyMG1vZGVybnxlbnwwfHx8fDE3Nzk0NTUzNTB8MA&ixlib=rb-4.1.0&q=85"];

  const specs = [
    { icon: Calendar, label: "Year", value: car.year },
    { icon: Gauge, label: "Mileage", value: formatKM(car.mileage) },
    { icon: Fuel, label: "Fuel", value: car.fuel },
    { icon: Settings2, label: "Transmission", value: car.transmission },
    { icon: Palette, label: "Color", value: car.color },
    { icon: MapPin, label: "Location", value: car.location },
  ];

  return (
    <div data-testid="car-detail-page" className="bg-[#FCFCFC]">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 pt-8">
        <Link to="/inventory" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-black" data-testid="back-to-inventory">
          <ArrowLeft size={15} strokeWidth={1.5} /> Back to inventory
        </Link>
      </div>

      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 pt-8 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Gallery */}
        <div className="lg:col-span-7">
          <div className="aspect-[4/3] bg-white border border-gray-200 overflow-hidden" data-testid="car-main-image">
            <img src={images[active]} alt={`${car.brand} ${car.model}`} className="w-full h-full object-cover" />
          </div>
          {images.length > 1 && (
            <div className="grid grid-cols-4 gap-3 mt-3">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  data-testid={`thumb-${i}`}
                  className={`aspect-[4/3] overflow-hidden border ${i === active ? "border-black" : "border-gray-200"}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="lg:col-span-5">
          <p className="overline text-[#9D8C70]">{car.brand}</p>
          <h1 className="font-display text-4xl md:text-5xl tracking-tighter font-light mt-2" data-testid="car-title">
            {car.model}
          </h1>
          <div className="mt-4 flex items-baseline gap-3">
            <span className="font-display text-3xl tracking-tight" data-testid="car-price">{formatINR(car.price)}</span>
            <span className="text-sm text-gray-500">· {car.year}</span>
          </div>

          <div className="grid grid-cols-2 gap-x-6 gap-y-5 mt-10 pt-8 border-t border-gray-200">
            {specs.map((s) => (
              <div key={s.label} className="flex items-start gap-3">
                <s.icon size={18} strokeWidth={1.5} className="mt-0.5 text-gray-500" />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500">{s.label}</p>
                  <p className="text-sm mt-1">{s.value}</p>
                </div>
              </div>
            ))}
          </div>

          <p className="text-gray-700 leading-relaxed text-sm mt-10 pt-8 border-t border-gray-200">{car.description}</p>

          {car.features?.length > 0 && (
            <div className="mt-8">
              <p className="overline mb-4">Highlights</p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {car.features.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle2 size={14} strokeWidth={1.5} className="text-[#9D8C70]" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Inquiry form */}
          <div className="mt-12 border border-gray-200 bg-white p-6 md:p-8" data-testid="inquiry-form-card">
            <p className="overline mb-2">Enquire about this car</p>
            <h3 className="font-display text-2xl tracking-tight mb-6">Reserve a private viewing</h3>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="iname" className="text-xs uppercase tracking-[0.15em] text-gray-600">Full name</Label>
                <Input id="iname" data-testid="inquiry-name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className="mt-2" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="iemail" className="text-xs uppercase tracking-[0.15em] text-gray-600">Email</Label>
                  <Input id="iemail" type="email" data-testid="inquiry-email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} className="mt-2" />
                </div>
                <div>
                  <Label htmlFor="iphone" className="text-xs uppercase tracking-[0.15em] text-gray-600">Phone</Label>
                  <Input id="iphone" data-testid="inquiry-phone" value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} className="mt-2" />
                </div>
              </div>
              <div>
                <Label htmlFor="imsg" className="text-xs uppercase tracking-[0.15em] text-gray-600">Message</Label>
                <Textarea id="imsg" data-testid="inquiry-message" rows={3} value={form.message} onChange={(e)=>setForm({...form,message:e.target.value})} className="mt-2" />
              </div>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button type="submit" disabled={submitting} data-testid="inquiry-submit" className="btn-primary rounded-sm flex-1 h-12">
                  {submitting ? "Sending…" : "Send Inquiry"}
                </Button>
                <a
                  href={WHATSAPP_LINK(`Hi Royale Yaan, I'm interested in the ${car.year} ${car.brand} ${car.model}.`)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="inquiry-whatsapp"
                  className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-sm border border-gray-300 hover:border-black transition-colors text-sm"
                >
                  <MessageCircle size={15} strokeWidth={1.5} /> WhatsApp
                </a>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
